import React, { useEffect, useState } from "react";

import HttpManager from "../../services/HttpManager";
import { ArticleDTO } from "../../models/ArticleDTO";
import { DeliveryPlan } from "../../services/v2/inventory.service";
import { DeliveryBatchEntry } from "../../services/v2/kits.service";
import { FIELD_FONT, requestErrorText } from "./kitUi";
import FirmaPad from "./firmaPad";
import FotoEntrega from "./fotoEntrega";
import PiecesPicker, { PiecePicks, piecesItems, piecesValid, presetPicks } from "./piecesPicker";
import EntregaVentaVieja, { LegacyDraft, emptyLegacy, legacyItems, legacyValid } from "./entregaVentaVieja";

export interface DeliveryBatchPromptProps {
	/** The products of the invoice still to deliver (the one clicked first). */
	kits: ArticleDTO[];
	invoiceIds: number[];
	campusId: number;
	busy?: boolean;
	error?: string | null;
	onConfirm: (entries: DeliveryBatchEntry[], signature: string, photo: File | null) => void;
	/** "Solo este producto": back to the one-product Registrar Entrega (notes, corrections, "ya se había entregado"). */
	onSingle: (kit: ArticleDTO) => void;
	onCancel: () => void;
}

interface Row {
	include: boolean;
	type: "total" | "partial";
	note: string;
	plan: DeliveryPlan | null;
	planError: string | null;
	loading: boolean;
	picked: PiecePicks;
	warehouseId: number | null;
	/** Old-catalog sale: what was given before + today's new-catalog products. */
	legacy: LegacyDraft;
}

/**
 * "Entregar Factura" (Esteban, 2026-10-29): lo que se compró en la misma factura se entrega junto, con UNA firma, una foto
 * opcional, un Comprobante con la lista y un solo WhatsApp. Solo productos pagados completos que faltan por entregar (las
 * mensualidades y servicios de la factura no aparecen). Cada producto se puede quitar; los del catálogo nuevo se entregan
 * pieza por pieza y descuentan inventario; lo que no tiene piezas (catálogo viejo, académicos) se entrega completo.
 */
const DeliveryBatchPrompt: React.FC<DeliveryBatchPromptProps> = ({ kits, invoiceIds, campusId, busy, error, onConfirm, onSingle, onCancel }) => {
	const [rows, setRows] = useState<Record<number, Row>>(() => {
		const initial: Record<number, Row> = {};
		kits.forEach((kit) => { initial[kit.id] = { include: true, type: "total", note: "", plan: null, planError: null, loading: true, picked: {}, warehouseId: null, legacy: emptyLegacy() }; });
		return initial;
	});
	const [signature, setSignature] = useState<string | null>(null);
	const [photo, setPhoto] = useState<File | null>(null);
	const [reloadKey, setReloadKey] = useState<number>(0);

	const patchRow = (kitId: number, changes: Partial<Row>) => setRows((previous) => ({ ...previous, [kitId]: { ...previous[kitId], ...changes } }));

	const loadPlan = (kitId: number, warehouseId: number | null, alive: () => boolean) => {
		patchRow(kitId, { loading: true, planError: null });
		HttpManager.getInstance().inventoryService.deliveryPlan(campusId, kitId, warehouseId)
			.then((response) => {
				if (!alive()) return;
				const plan = response.data;
				const tracked = plan.tracksInventory !== false && plan.lines.length > 0;
				patchRow(kitId, { plan, loading: false, picked: tracked ? presetPicks(plan) : {}, warehouseId: warehouseId ?? (plan.warehouse ? plan.warehouse.id : null) });
			})
			.catch((requestError) => {
				if (!alive()) return;
				patchRow(kitId, { plan: null, loading: false, include: false, planError: requestErrorText(requestError, "No se pudo cargar lo que falta entregar.") });
			});
	};

	useEffect(() => {
		let alive = true;
		kits.forEach((kit) => loadPlan(kit.id, null, () => alive));
		return () => { alive = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, reloadKey]);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel(); };
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [busy, onCancel]);

	const legacyOf = (row: Row) => !!row.plan && !trackedOf(row) && !!row.plan.legacy;
	const trackedOf = (row: Row) => !!row.plan && row.plan.tracksInventory !== false && row.plan.lines.length > 0;
	/** Why a product cannot go in this delivery (shown under it, and it stays unchecked). */
	const blockOf = (row: Row): string | null => {
		if (row.loading) return null;
		if (row.planError) return row.planError;
		if (row.plan && row.plan.blockedReason) return row.plan.blockedReason;
		if (row.plan && trackedOf(row) && row.plan.lines.every((line) => line.pending < 1)) return "Ya se entregó todo lo que compone este kit.";
		return null;
	};
	const rowValid = (row: Row): boolean => {
		if (row.loading || blockOf(row)) return false;
		if (trackedOf(row)) return piecesValid(row.plan, row.picked);
		if (legacyOf(row)) return legacyValid(row.legacy);
		return true; // no pieces: delivered whole (Esteban 2026-10-29: "Parcial" only for new kits, piece by piece)
	};

	const included = kits.filter((kit) => rows[kit.id] && rows[kit.id].include && !blockOf(rows[kit.id]));
	const loadingAny = kits.some((kit) => rows[kit.id]?.loading);
	const allValid = included.length > 0 && included.every((kit) => rowValid(rows[kit.id]));
	const first = kits[0];
	const receiver = first.isThirdParty ? (first.buyerName || "el comprador") : (first.studentName || "el alumno");

	const confirm = () => {
		if (!allValid || !signature) return;
		const entries: DeliveryBatchEntry[] = included.map((kit) => {
			const row = rows[kit.id];
			if (trackedOf(row)) return { kitId: kit.id, items: piecesItems(row.picked), warehouseId: row.warehouseId, note: row.note.trim() || undefined };
			if (legacyOf(row)) return { kitId: kit.id, type: row.legacy.type, previous: row.legacy.previous.trim() || undefined, items: legacyItems(row.legacy), warehouseId: row.plan!.warehouse?.id ?? null };
			return { kitId: kit.id, type: "total", note: row.note.trim() || undefined };
		});
		onConfirm(entries, signature, photo);
	};

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}
	>
		<div className="card" style={{ width: "100%", maxWidth: "720px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "8px" }}>Entregar Factura</h4>
				<div style={{ background: "var(--wa-papel-2)", borderRadius: "8px", padding: "10px 12px", marginBottom: "12px", fontSize: "13px", lineHeight: 1.5 }}>
					<div style={{ fontSize: "15px", fontWeight: 700, color: "var(--wa-tinta)" }}>{first.isThirdParty ? (first.buyerName || "Venta a terceros sin datos del comprador") : first.studentName}</div>
					<div style={{ color: "var(--wa-apagado)" }}>
						{first.isThirdParty ? "Venta a terceros" + (first.buyerPhone ? " · " + first.buyerPhone : "") : first.studentCode}
						{invoiceIds.length ? " · Factura " + invoiceIds.map((id) => "#" + id).join(", ") : ""}
					</div>
					<div style={{ marginTop: "4px" }}>{kits.length} productos pagados por entregar. Desmarca lo que no se lleva hoy.</div>
				</div>

				{kits.map((kit) => {
					const row = rows[kit.id];
					if (!row) return null;
					const block = blockOf(row);
					const tracked = trackedOf(row);
					const on = row.include && !block;
					return <div key={kit.id} style={{ border: "1px solid " + (on ? "var(--wa-acento)" : "var(--wa-filo)"), borderRadius: "10px", padding: "10px 12px", marginBottom: "10px", background: on ? "var(--wa-superficie)" : "var(--wa-papel-2)" }}>
						<div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
							<input type="checkbox" checked={on} disabled={busy || !!block || row.loading} aria-label={"Entregar " + (kit.name || kit.title)}
								onChange={(event) => patchRow(kit.id, { include: event.target.checked })} style={{ width: "18px", height: "18px", marginTop: "3px", flex: "0 0 18px" }} />
							<div style={{ flex: 1, minWidth: 0 }}>
								<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
									<span style={{ fontWeight: 700, fontSize: "14px", color: "var(--wa-tinta)" }}>{kit.name || kit.title}{kit.quantity > 1 ? <span style={{ fontWeight: 400, color: "var(--wa-apagado)" }}> × {kit.quantity}</span> : null}</span>
									<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} disabled={busy} onClick={() => onSingle(kit)}>Solo este producto</button>
								</div>
								<div style={{ fontSize: "11px", color: "var(--wa-apagado)" }}>SKU {kit.sku}{kit.deliveryStatusId === 2 ? " · ya lleva entrega parcial" : ""}</div>
								{row.loading ? <div style={{ fontSize: "12px", color: "var(--wa-apagado)", marginTop: "6px" }}>Cargando lo que falta entregar…</div>
									: block ? <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--wa-oro)", marginTop: "6px" }}>{block}</div>
									: !on ? null
									: tracked ? <div style={{ marginTop: "8px" }}>
										{(row.plan!.warehouseOptions || []).length > 1 ? <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" }}>
											<span style={{ fontSize: "13px", fontWeight: 600 }}>Sale de</span>
											<select className="form-select" style={{ height: "34px", fontSize: FIELD_FONT, width: "auto", flex: "1 1 200px", minWidth: 0 }} disabled={busy}
												value={row.warehouseId === null ? "" : String(row.warehouseId)}
												onChange={(event) => { const id = Number(event.target.value); patchRow(kit.id, { warehouseId: id }); loadPlan(kit.id, id, () => true); }}>
												{(row.plan!.warehouseOptions || []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
											</select>
										</div> : null}
										<PiecesPicker plan={row.plan!} picked={row.picked} onChange={(picked) => patchRow(kit.id, { picked })} busy={busy} />
									</div>
									: legacyOf(row) ? <div style={{ marginTop: "8px" }}>
										{(row.plan!.warehouseOptions || []).length > 1 ? <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" }}>
											<span style={{ fontSize: "13px", fontWeight: 600 }}>Sale de</span>
											<select className="form-select" style={{ height: "34px", fontSize: FIELD_FONT, width: "auto", flex: "1 1 200px", minWidth: 0 }} disabled={busy}
												value={row.warehouseId === null ? "" : String(row.warehouseId)}
												onChange={(event) => { const id = Number(event.target.value); patchRow(kit.id, { warehouseId: id }); loadPlan(kit.id, id, () => true); }}>
												{(row.plan!.warehouseOptions || []).map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>)}
											</select>
										</div> : null}
										<EntregaVentaVieja campusId={campusId} warehouse={row.plan!.warehouse || null} value={row.legacy} onChange={(legacy) => patchRow(kit.id, { legacy })} busy={busy} />
									</div>
									: null}
							</div>
						</div>
					</div>;
				})}
				{kits.some((kit) => rows[kit.id]?.planError) ? <button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy || loadingAny} onClick={() => setReloadKey(reloadKey + 1)}>Reintentar</button> : null}

				<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "12px", marginBottom: "6px" }}>{first.isThirdParty ? "Firma de Quien Recibe" : "Firma del Alumno"} <span style={{ color: "var(--wa-oro)" }}>*</span></div>
				<FirmaPad value={signature} onChange={setSignature} disabled={busy} studentName={receiver} />
				<div style={{ fontWeight: 600, fontSize: "13px", marginTop: "12px", marginBottom: "6px" }}>Foto o Comprobante (Opcional)</div>
				<FotoEntrega value={photo} onChange={setPhoto} disabled={busy} />

				{error ? <div className="alert alert-danger" style={{ marginTop: "10px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "8px", marginTop: "14px", flexWrap: "wrap" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel} style={{ flex: "0 0 auto" }}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" onClick={confirm} style={{ flex: "0 1 auto", whiteSpace: "nowrap", maxWidth: "100%" }}
						disabled={busy || loadingAny || !allValid || !signature}>
						{busy ? "Guardando…" : included.length === 1 ? "Entregar 1 Producto" : "Entregar " + included.length + " Productos"}
					</button>
				</div>
				<div style={{ fontSize: "12px", color: "var(--wa-apagado)", textAlign: "right", marginTop: "6px" }}>
					{first.isThirdParty && !first.buyerPhone ? "Sin teléfono del comprador: el comprobante no se manda por WhatsApp." : "Un solo comprobante con todo por WhatsApp de Caja."}
				</div>
			</div>
		</div>
	</div>;
};

export default DeliveryBatchPrompt;
